import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../services/api';
import { debug } from '../utils/debug';
import type { User } from '../types';

interface UseAuthReturn {
  isLoading: boolean;
  isAuthenticated: boolean;
  user: User | null;
  error: string | null;
  retry: () => Promise<void>;
}

export const useAuth = (): UseAuthReturn => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);

  const verifyAuth = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      debug.log('🔐 useAuth - Verifying authentication...');

      const response = await apiClient.verifyAuth();

      if (response.success && response.authenticated && response.data?.user) {
        debug.log('✅ useAuth - Authentication successful:', {
          userId: response.data.user.id,
          telegramId: response.data.user.telegram_id,
        });

        setIsAuthenticated(true);
        setUser(response.data.user);
        setError(null);
      } else {
        debug.warn('❌ useAuth - Authentication failed:', response.error);

        setIsAuthenticated(false);
        setUser(null);
        setError(response.error || 'Authentication failed');
      }
    } catch (err) {
      debug.error('❌ useAuth - Error verifying authentication:', err);

      setIsAuthenticated(false);
      setUser(null);
      setError('Failed to verify authentication. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const retry = useCallback(async () => {
    debug.log('🔄 useAuth - Retrying authentication...');
    await verifyAuth();
  }, [verifyAuth]);

  useEffect(() => {
    verifyAuth();
  }, [verifyAuth]);

  return {
    isLoading,
    isAuthenticated,
    user,
    error,
    retry,
  };
};

